import React from "react";
import { ArrowRight, Phone, Shield, Star, MapPin } from "lucide-react";
import { BUSINESS, HERO_IMAGE } from "../lib/constants";

export default function Hero() {
  const scrollToQuote = () => {
    const el = document.getElementById("quote");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-32 pb-24 md:pt-40 md:pb-32 overflow-hidden"
    >
      {/* Background image */}
      <div className="absolute inset-0 -z-10">
        <img
          src={HERO_IMAGE}
          alt="5-Star Handyman Service owner drilling in a Southwest Florida home — handyman in Lehigh Acres, Fort Myers and Cape Coral, FL."
          fetchPriority="high"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#14110F] via-[#14110F]/80 to-[#14110F]/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#14110F] via-transparent to-transparent" />
        <div className="absolute inset-0 bg-grain opacity-60" />
      </div>

      <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-12 items-end">
        <div className="lg:col-span-8">
          <div
            data-testid="hero-badge"
            className="inline-flex items-center gap-2 border border-[#F5A623]/40 bg-[#F5A623]/10 text-[#F5A623] px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-[0.2em] mb-8"
          >
            <Star className="w-3.5 h-3.5 fill-[#F5A623]" />
            Family-Owned · Lehigh Acres, FL
          </div>

          <h1
            data-testid="hero-headline"
            className="font-display text-[44px] leading-[1.02] md:text-7xl lg:text-[88px] font-bold text-[#F9FAFB] uppercase tracking-tighter"
          >
            <span className="sr-only">
              5-Star Handyman Service — handyman in Lehigh Acres, Fort Myers
              and Cape Coral, Florida. Licensed, insured, family-owned.
            </span>
            <span aria-hidden="true">
              Honest hands.
              <br />
              <span className="text-[#F5A623]">Five-star</span> work.
            </span>
          </h1>

          <p
            data-testid="hero-subtext"
            className="mt-8 max-w-2xl text-lg md:text-xl text-[#A19D98] leading-relaxed"
          >
            {BUSINESS.tagline} Family-owned handyman serving{" "}
            <strong className="text-[#F9FAFB] font-medium">Lehigh Acres</strong>,{" "}
            <strong className="text-[#F9FAFB] font-medium">Fort Myers</strong>{" "}
            and{" "}
            <strong className="text-[#F9FAFB] font-medium">Cape Coral, FL</strong>{" "}
            — from ceiling fans and door installs to gutter cleanings and
            everyday home repairs.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
            <button
              data-testid="hero-quote-cta"
              onClick={scrollToQuote}
              className="group inline-flex items-center justify-center gap-2 bg-[#F5A623] text-[#14110F] hover:bg-[#D98C1A] px-7 py-4 rounded-full text-sm font-bold uppercase tracking-[0.15em] transition-colors"
            >
              Request a Free Quote
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </button>
            <a
              href={`tel:${BUSINESS.phoneRaw}`}
              data-testid="hero-call-cta"
              className="inline-flex items-center justify-center gap-2 border border-[#F5A623] text-[#F5A623] hover:bg-[#F5A623] hover:text-[#14110F] px-7 py-4 rounded-full text-sm font-bold uppercase tracking-[0.15em] transition-colors"
            >
              <Phone className="w-4 h-4" />
              {BUSINESS.phone}
            </a>
          </div>
        </div>

        <div className="lg:col-span-4 grid grid-cols-2 gap-4">
          <TrustCard
            icon={<Star className="w-5 h-5 fill-[#F5A623] text-[#F5A623]" />}
            label="5-star service"
            value="Every visit, every job"
          />
          <TrustCard
            icon={<Shield className="w-5 h-5 text-[#F5A623]" />}
            label="Licensed & insured"
            value="Peace of mind built-in"
          />
          <TrustCard
            icon={<MapPin className="w-5 h-5 text-[#F5A623]" />}
            label="Locally owned"
            value="Lehigh Acres & Fort Myers"
          />
          <TrustCard
            icon={
              <span className="font-display text-[#F5A623] text-xl font-bold">
                ⌂
              </span>
            }
            label="On-time, clean up"
            value="We leave it better"
          />
        </div>
      </div>
    </section>
  );
}

function TrustCard({ icon, label, value }) {
  return (
    <div
      data-testid={`trust-card-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      className="bg-[#1E1A18]/70 backdrop-blur-sm border border-[#332C27] rounded-2xl p-5 hover:border-[#F5A623]/40 transition-colors"
    >
      <div className="w-9 h-9 rounded-lg bg-[#F5A623]/10 border border-[#F5A623]/20 flex items-center justify-center mb-3">
        {icon}
      </div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#F5A623] font-bold">
        {label}
      </div>
      <div className="text-sm text-[#F9FAFB] mt-1 font-medium leading-snug">
        {value}
      </div>
    </div>
  );
}
